import React from 'react'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAppSelector } from '@Hooks/ReduxHooks'
import usePlatformFlags from '@Hooks/usePlatformFlags'
import { useGetUserProfileQuery } from '@Features/Dashboard/DashboardSlice/Dashboard.slice'
import { useGetNewsFeedQuery } from '@Features/LandingPage/LandingPageApi/LandingPageApi'
import { Avatar as AbwAvatar, Button as AbwButton, Icon as AbwIcon } from '@Components/AbwaabPlus'

/* ─────────────────────────── Welcome banner ─────────────────────────── */

export const HomeWelcome: React.FC = () => {
	const [t] = useTranslation('landingPage', { keyPrefix: 'student_home' })
	const { user } = useAppSelector(s => s.auth)
	const { data } = useGetUserProfileQuery()
	const firstName = data?.data?.first_name ?? user?.first_name ?? ''
	const photo = data?.data?.profile_photo

	return (
		<WelcomeBanner>
			<AbwAvatar src={photo || undefined} emoji="🧑‍🎓" color="#FFFFFF" tint={0.2} size={56} shape="circle" />
			<div className="txt">
				<div className="hi">{t('welcome', { name: firstName, defaultValue: `مرحباً، ${firstName}!` })}</div>
				<div className="sub">{t('welcome_sub', { defaultValue: 'أنت على المسار الصحيح — كمّل من حيث وقفت.' })}</div>
			</div>
		</WelcomeBanner>
	)
}

const WelcomeBanner = styled.div`
	background: linear-gradient(135deg, var(--blue), var(--navy));
	border-radius: 16px;
	padding: 20px 16px;
	display: flex;
	align-items: center;
	gap: 14px;
	.txt {
		flex: 1;
		min-width: 0;
		display: flex;
		flex-direction: column;
		gap: 2px;
		text-align: start;
	}
	.hi {
		font: var(--fw-bold) 18px var(--font-sans);
		color: var(--text-on-brand);
	}
	.sub {
		font: var(--fw-regular) 13px var(--font-sans);
		color: rgba(255, 255, 255, 0.8);
	}
`

/* ──────────────────────── Profile-completion card ───────────────────── */

/**
 * Shown only while something is missing. Steps mirror the signup machine:
 * the account exists (always done), phone verification (/signup/1, the
 * VerifyPhoneForm step) and بياناتك (/signup/2, the CompleteProfile step).
 */
export const HomeProfileSteps: React.FC = () => {
	const [t] = useTranslation('landingPage', { keyPrefix: 'student_home' })
	const navigate = useNavigate()
	const { data } = useGetUserProfileQuery()
	const profile = data?.data
	if (!profile) return null

	const steps = [
		{ label: t('step_account', { defaultValue: 'أنشئ حسابك' }), done: true, to: undefined as string | undefined },
		{ label: t('step_phone', { defaultValue: 'أكّد رقم هاتفك' }), done: !!profile.phone_verified, to: '/signup/1' },
		{ label: t('step_data', { defaultValue: 'أكمل بياناتك' }), done: !!profile.student?.profile_complete, to: '/signup/2' },
	]
	const done = steps.filter(s => s.done).length
	if (done === steps.length) return null

	return (
		<StepsCard>
			<div className="head">
				<div className="title">{t('steps_title', { defaultValue: 'أكمل ملفك — خطوة واحدة فاضلة!' })}</div>
				<span className="count" dir="ltr">
					{done}/{steps.length}
				</span>
			</div>
			<ProgressTrack>
				<span style={{ width: `${Math.round((done / steps.length) * 100)}%` }} />
			</ProgressTrack>
			<div className="list">
				{steps.map(s => (
					<StepRow key={s.label} type="button" data-done={s.done ? 'true' : undefined} onClick={() => !s.done && s.to && navigate(s.to)}>
						<span className="dot">
							<AbwIcon name={s.done ? 'check-circle' : 'user'} size={12} />
						</span>
						<span className="label">{s.label}</span>
						{!s.done && (
							<span className="chev">
								<AbwIcon name="chevron-left" size={14} />
							</span>
						)}
					</StepRow>
				))}
			</div>
		</StepsCard>
	)
}

const StepsCard = styled.div`
	background: var(--surface-card);
	border: 1px solid var(--border);
	border-radius: 16px;
	box-shadow: var(--sh-rest);
	padding: 16px;
	display: flex;
	flex-direction: column;
	gap: 12px;
	.head {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 8px;
	}
	.title {
		font: var(--fw-bold) 14px var(--font-sans);
		color: var(--text-primary);
		text-align: start;
	}
	.count {
		font: var(--fw-bold) 12px var(--font-sans);
		color: var(--blue);
	}
	.list {
		display: flex;
		flex-direction: column;
		gap: 8px;
	}
`
const ProgressTrack = styled.div`
	height: 8px;
	border-radius: var(--r-full);
	background: var(--surface-sunken-strong);
	overflow: hidden;
	span {
		display: block;
		height: 100%;
		border-radius: var(--r-full);
		background: var(--blue);
		transition: width var(--dur-fast) var(--ease);
	}
`
const StepRow = styled.button`
	border: none;
	background: none;
	padding: 0;
	display: flex;
	align-items: center;
	gap: 10px;
	cursor: pointer;
	font-family: var(--font-sans);
	text-align: start;
	.dot {
		width: 22px;
		height: 22px;
		border-radius: var(--r-full);
		display: flex;
		align-items: center;
		justify-content: center;
		background: var(--surface-sunken-strong);
		color: var(--text-secondary);
	}
	.label {
		flex: 1;
		font: var(--fw-bold) 13px var(--font-sans);
		color: var(--text-primary);
	}
	.chev {
		color: var(--text-disabled);
		display: flex;
		html:not([dir='rtl']) & {
			transform: scaleX(-1);
		}
	}
	&[data-done='true'] {
		cursor: default;
		.dot {
			background: var(--success-10);
			color: var(--success-dark);
		}
		.label {
			font-weight: var(--fw-regular);
			color: var(--text-secondary);
		}
	}
`

/* ─────────────────── Admin banners (NewsFeeds, design frame) ─────────── */

/** The admin NewsFeeds image banners in the design's rounded promo slot —
 *  swipeable when there is more than one; each opens its redirection URL. */
export const HomeBanners: React.FC = () => {
	const { data } = useGetNewsFeedQuery()
	const feeds = (data?.data ?? []).filter(n => n.is_active !== false)
	if (feeds.length === 0) return null

	return (
		<BannersRail data-single={feeds.length === 1 ? 'true' : undefined}>
			{feeds.map(news => (
				<BannerCard key={news.id} type="button" onClick={() => news.redirection_url && window.open(news.redirection_url, '_blank')}>
					<picture>
						{news.photo_sm && <source media="(max-width: 767px)" srcSet={news.photo_sm} />}
						<img src={news.photo} alt="" loading="lazy" />
					</picture>
				</BannerCard>
			))}
		</BannersRail>
	)
}

const BannersRail = styled.div`
	display: flex;
	gap: 8px;
	overflow-x: auto;
	scroll-snap-type: x mandatory;
	scrollbar-width: none;
	-ms-overflow-style: none;
	&::-webkit-scrollbar {
		display: none;
	}
	> * {
		flex: 0 0 100%;
		scroll-snap-align: start;
	}
`
const BannerCard = styled.button`
	border: none;
	padding: 0;
	background: none;
	cursor: pointer;
	border-radius: 16px;
	overflow: hidden;
	display: block;
	img {
		width: 100%;
		display: block;
	}
`

/* ─────────────────────────── Search bridge ──────────────────────────── */

export const HomeSearchBridge: React.FC = () => {
	const [t] = useTranslation('landingPage', { keyPrefix: 'student_home' })
	const navigate = useNavigate()
	const { classroomsEnabled, coursesEnabled } = usePlatformFlags()
	// Copy follows the platform mode; courses-only routes straight to the tab.
	const title = !classroomsEnabled
		? t('search_title_courses', { defaultValue: 'بتدور على كورس جديد؟' })
		: coursesEnabled
		? t('search_title_hybrid', { defaultValue: 'بتدور على فصل أو كورس جديد؟' })
		: t('search_title', { defaultValue: 'بتدور على فصل جديد؟' })
	const sub = !classroomsEnabled
		? t('search_sub_courses', { defaultValue: 'ابحث في الكورسات والمدرسين من استكشف.' })
		: coursesEnabled
		? t('search_sub_hybrid', { defaultValue: 'ابحث في الفصول والكورسات والمدرسين حسب صفّك من استكشف.' })
		: t('search_sub', { defaultValue: 'ابحث في الفصول والمدرسين حسب صفّك من استكشف.' })
	return (
		<BridgeCard type="button" onClick={() => navigate(classroomsEnabled ? '/search' : '/search?tab=courses')}>
			<span className="tile">
				<AbwIcon name="search" size={20} />
			</span>
			<span className="txt">
				<span className="title">{title}</span>
				<span className="sub">{sub}</span>
			</span>
			<span className="chev">
				<AbwIcon name="chevron-left" size={18} />
			</span>
		</BridgeCard>
	)
}

const BridgeCard = styled.button`
	border: 1px solid var(--border);
	background: var(--surface-card);
	border-radius: 16px;
	box-shadow: var(--sh-rest);
	padding: 16px;
	display: flex;
	align-items: center;
	gap: 12px;
	cursor: pointer;
	font-family: var(--font-sans);
	transition: box-shadow var(--dur-fast) var(--ease);
	&:hover {
		box-shadow: var(--sh-hover);
	}
	.tile {
		flex: none;
		width: 44px;
		height: 44px;
		border-radius: 12px;
		background: var(--blue-10);
		color: var(--blue);
		display: flex;
		align-items: center;
		justify-content: center;
	}
	.txt {
		flex: 1;
		min-width: 0;
		display: flex;
		flex-direction: column;
		gap: 2px;
		text-align: start;
	}
	.title {
		font: var(--fw-bold) 14px var(--font-sans);
		color: var(--text-primary);
	}
	.sub {
		font: var(--fw-regular) 12px var(--font-sans);
		color: var(--text-secondary);
	}
	.chev {
		color: var(--text-disabled);
		display: flex;
		html:not([dir='rtl']) & {
			transform: scaleX(-1);
		}
	}
`

/* ───────────────────── Empty state (no enrollments) ─────────────────── */

export const HomeEmptyState: React.FC = () => {
	const [t] = useTranslation('landingPage', { keyPrefix: 'student_home' })
	const navigate = useNavigate()
	const { classroomsEnabled } = usePlatformFlags()
	return (
		<EmptyCard>
			<span className="emoji" aria-hidden>
				🎓
			</span>
			<span className="title">
				{classroomsEnabled
					? t('empty_title', { defaultValue: 'لسه مشتركتش في فصل؟' })
					: t('empty_title_courses', { defaultValue: 'لسه مشتركتش في كورس؟' })}
			</span>
			<span className="sub">
				{classroomsEnabled
					? t('empty_sub', { defaultValue: 'ابحث عن الفصل الافتراضي المخصص لصفّك وابدأ رحلتك.' })
					: t('empty_sub_courses', { defaultValue: 'استكشف الكورسات المتاحة وابدأ رحلتك.' })}
			</span>
			<AbwButton variant="primary" onClick={() => navigate(classroomsEnabled ? '/search' : '/search?tab=courses')}>
				{classroomsEnabled
					? t('empty_cta', { defaultValue: 'ابحث عن فصلك' })
					: t('empty_cta_courses', { defaultValue: 'استكشف الكورسات' })}
			</AbwButton>
		</EmptyCard>
	)
}

const EmptyCard = styled.div`
	background: var(--surface-card);
	border: 1px solid var(--border);
	border-radius: 16px;
	box-shadow: var(--sh-rest);
	padding: 24px 16px;
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 10px;
	text-align: center;
	.emoji {
		font-size: 40px;
		line-height: 1;
	}
	.title {
		font: var(--fw-bold) 15px var(--font-sans);
		color: var(--text-primary);
	}
	.sub {
		font: var(--fw-regular) 13px/1.7 var(--font-sans);
		color: var(--text-secondary);
	}
`
